import { WebColorsSettings } from '../types/settingsTypes';
import LocalStorageUtils from './local-storage';

const WEB_COLORS_SETTINGS_STORAGE_KEY = 'webColorsSettings';

const webColorsToCssMap: Record<keyof WebColorsSettings, string> = {
  InputBorderColor: '--input-border-color',
  UiSymbolsColor: '--ui-symbols-color',
  HeadersTextColor: '--headers-text-color',
  ActiveTabTextLinkColor: '--active-tab-text-link-color',
  ButtonFilledTextColor: '--button-filled-text-color',
  ButtonFilledBgColor: '--button-filled-bg-color',
  ButtonHoverColor: '--button-hover-color',
  DropdownSelectCellHoverColor: '--dropdown-select-cell-hover-color',
  MainMenuBgLightGradientColor: '--main-menu-bg-ligth-gradient-color',
  MainMenuBgDarkGradientColor: '--main-menu-bg-dark-gradient-color',
  ModalFooterBgColor: '--modal-footer-bg-color',
  ModalTitleTextColor: '--modal-title-text-color',
  ModalGraphColor: '--modal-graph-color',
  ModalGraphRgbaColor: '--modal-graph-rgba-color'
};

const applyCssVariables = (values: Record<string, string | null | undefined>): void => {
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === 'string' && value.length) {
      document.documentElement.style.setProperty(key, value);
    }
  });
};

const normalizeWebColorsSettings = (settings: Partial<WebColorsSettings> | null | undefined): WebColorsSettings => {
  return {
    ...new WebColorsSettings(),
    ...(settings || {})
  };
};

const persistWebColorsSettings = (settings: WebColorsSettings): void => {
  try {
    LocalStorageUtils.putValueToLocalStorage(WEB_COLORS_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors: runtime colors are still applied below.
  }
};

const getStoredWebColorsSettings = (): WebColorsSettings | null => {
  try {
    const rawSettings = LocalStorageUtils.getValueFromLocalStorage(WEB_COLORS_SETTINGS_STORAGE_KEY);

    if (!rawSettings) {
      return null;
    }

    const parsedSettings = JSON.parse(rawSettings) as Partial<WebColorsSettings> | null;

    if (!parsedSettings || typeof parsedSettings !== 'object') {
      return null;
    }

    return normalizeWebColorsSettings(parsedSettings);
  } catch {
    return null;
  }
};

const setNormalizedColorsFromWebSettings = (settings: WebColorsSettings): void => {
  const cssVars = Object.entries(webColorsToCssMap).reduce((acc, [settingsField, cssVarName]) => {
    acc[cssVarName] = settings[settingsField as keyof WebColorsSettings] as string | undefined;
    return acc;
  }, {} as Record<string, string | undefined>);

  applyCssVariables(cssVars);
};

export const cacheAndSetColorsFromWebSettings = (settings: Partial<WebColorsSettings>): void => {
  const normalizedSettings = normalizeWebColorsSettings(settings);

  persistWebColorsSettings(normalizedSettings);
  setNormalizedColorsFromWebSettings(normalizedSettings);
};

export const applyCachedWebColorsSettings = (): void => {
  const storedSettings = getStoredWebColorsSettings();

  if (!storedSettings) {
    return;
  }

  setNormalizedColorsFromWebSettings(storedSettings);
};
